/**
 * @fileoverview A list whose items come and go smoothly (owner, 2026-10-16:
 * "clicking mark, postpone, skip causes the screen to jump ---- there should
 * be a smooth removal or crossing out, somewhat animated"). An item that
 * leaves (a job postponed off Today) folds away: its height closes and it
 * fades, so the items under it slide up rather than jump. An item that
 * arrives after the list first showed (the same job under Coming up) opens
 * the same way in reverse. Items there from the start just show.
 *
 * The leaving item is the last thing it showed, kept for LIST_LEAVE_MS and
 * then dropped. Under prefers-reduced-motion nothing moves: items come and go
 * at once, as before.
 *
 * The height is the grid-rows trick (an item's row from 1fr to 0fr, its
 * content clipped), so no item is measured. The keyframes are Tailwind's
 * (tailwind.config.js grow-fold-in, grow-fold-out).
 *
 * Where: TodoGroups (Today), PlantJobs (the top of Plants), BedJobs (a bed's
 * card).
 *
 * @module AnimatedList
 */

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/** How long a leaving item takes to fold away, in milliseconds (the grow-fold-out animation's length). */
export const LIST_LEAVE_MS = 280;

/** One item: a stable key, and what it shows. */
export interface AnimatedItem {
  key: string;
  node: React.ReactNode;
}

/** What AnimatedList is given. */
interface AnimatedListProps {
  /** The items now, in order. */
  items: AnimatedItem[];
  /** Classes for the list (its spacing: space-y, gap, grid, divide). */
  className?: string;
  /** Classes for each item. */
  itemClassName?: string;
  /** The list's accessible name, when it has one. */
  'aria-label'?: string;
}

/**
 * The list.
 *
 * @param props - AnimatedListProps.
 */
export const AnimatedList: React.FC<AnimatedListProps> = ({ items, className, itemClassName, 'aria-label': ariaLabel }) => {
  /** The items leaving, each with the place it had. */
  const [leaving, setLeaving] = useState<Array<AnimatedItem & { index: number }>>([]);
  /** What each item showed last time, and their order: what a leaving item keeps showing. */
  const last = useRef<{ nodes: Map<string, React.ReactNode>; order: string[] }>({ nodes: new Map(), order: [] });
  /** The keys there when the list first showed, and the ones that have opened since: shown as they are. */
  const first = useRef<Set<string> | null>(null);
  /** Draws again once an item has opened, so it is no longer clipped. */
  const [, setOpened] = useState(0);
  if (first.current === null) first.current = new Set(items.map((item) => item.key));
  const keysText = items.map((item) => item.key).join('|');

  // When the keys change: the ones gone fold away from their old place, then are dropped.
  useEffect(() => {
    const now = new Set(items.map((item) => item.key));
    const gone = last.current.order.filter((key) => !now.has(key));
    const timers: number[] = [];
    if (gone.length > 0) {
      setLeaving((current) => [
        ...current.filter((item) => !now.has(item.key) && !gone.includes(item.key)),
        ...gone.map((key) => ({ key, node: last.current.nodes.get(key), index: last.current.order.indexOf(key) })),
      ]);
      timers.push(window.setTimeout(() => setLeaving((current) => current.filter((item) => !gone.includes(item.key))), LIST_LEAVE_MS));
    }
    // One that came back while leaving shows at once.
    setLeaving((current) => (current.some((item) => now.has(item.key)) ? current.filter((item) => !now.has(item.key)) : current));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
    // Only when the keys change; what they show is kept below on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysText]);

  // What each item shows now, for when it leaves.
  useEffect(() => {
    last.current = { nodes: new Map(items.map((item) => [item.key, item.node])), order: items.map((item) => item.key) };
  });

  /** The items now with the leaving ones at their old places. */
  const shown: Array<AnimatedItem & { state: 'stay' | 'in' | 'out' }> = items.map((item) => ({
    ...item,
    state: first.current?.has(item.key) ? 'stay' : 'in',
  }));
  for (const item of [...leaving].sort((a, b) => a.index - b.index)) {
    shown.splice(Math.min(item.index, shown.length), 0, { ...item, state: 'out' });
  }

  return (
    <ul className={className} aria-label={ariaLabel}>
      {shown.map((item) => (
        <li
          key={item.key}
          className={cn(
            itemClassName,
            'grid grid-rows-[1fr]',
            item.state === 'in' && 'motion-safe:animate-grow-fold-in',
            item.state === 'out' && 'pointer-events-none motion-safe:animate-grow-fold-out motion-reduce:hidden',
          )}
          aria-hidden={item.state === 'out' || undefined}
          onAnimationEnd={item.state === 'in' ? () => { first.current?.add(item.key); setOpened((n) => n + 1); } : undefined}
        >
          {/* Clipped only while it opens or folds, so a card's own shadow and focus ring show otherwise. */}
          <div className={item.state === 'stay' ? undefined : 'min-h-0 overflow-hidden'}>{item.node}</div>
        </li>
      ))}
    </ul>
  );
};
